import { format } from 'date-fns';
import type { Lesson, LessonOverride, Term } from '../types';
import { generateClinicOccurrences } from './lessonDates';

type DateRangeSource = Pick<Term, 'startDate' | 'endDate'>;

function resolveDateRange(
  lesson: Pick<Lesson, 'lessonType' | 'startDate' | 'endDate'>,
  term: DateRangeSource | null
): DateRangeSource | null {
  if (term != null) return term;
  if (lesson.lessonType === 'camp' && lesson.startDate && lesson.endDate) {
    return { startDate: lesson.startDate, endDate: lesson.endDate };
  }
  return null;
}

/** Upcoming scheduled session dates (YYYY-MM-DD), oldest first. */
export function getUpcomingBookableSessionDates(
  lesson: Pick<Lesson, 'id' | 'lessonType' | 'daysOfWeek' | 'startTime' | 'endTime' | 'startDate' | 'endDate'>,
  term: DateRangeSource | null,
  excludedDates: string[] = [],
  overrides: LessonOverride[] = []
): string[] {
  const dateRange = resolveDateRange(lesson, term);
  if (!dateRange) return [];

  const excluded = new Set(excludedDates.map((d) => d.slice(0, 10)));
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const occurrences = generateClinicOccurrences(lesson, dateRange, overrides);
  return occurrences
    .filter((occ) => occ.status === 'scheduled' && occ.date >= today)
    .map((occ) => format(occ.date, 'yyyy-MM-dd'))
    .filter((ymd) => !excluded.has(ymd));
}

/** First upcoming session date for drop-in booking, or null when none remain. */
export function getNextAvailableDropInSessionDate(
  lesson: Pick<Lesson, 'id' | 'lessonType' | 'daysOfWeek' | 'startTime' | 'endTime' | 'startDate' | 'endDate'>,
  term: DateRangeSource | null,
  excludedDates: string[] = [],
  overrides: LessonOverride[] = []
): string | null {
  const dates = getUpcomingBookableSessionDates(lesson, term, excludedDates, overrides);
  return dates[0] ?? null;
}
